import React, { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Link, useParams } from "react-router-dom"
import { Alert, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb2"
import CampaignPermissionNotice from "components/Common/CampaignPermissionNotice"
import useCurrentPermissions from "hooks/useCurrentPermissions"
import { get, post } from "helpers/api_helper"
import { getApiErrorMessage } from "helpers/api_error_helper"
import { channelLabel, couponApi, displayDate, parseCouponCodes, safeEntryUrl } from "./couponHelpers"

const statusLabels = {
  awaiting_stock: "Awaiting stock", pending: "Pending", sending: "Sending", sent: "Sent",
  failed: "Failed", expired: "Expired", cancelled: "Cancelled", manual_review: "Manual review",
}

function useCouponRows(id, resource, revision, enabled = true) {
  const [page, setPage] = useState(1)
  const [result, setResult] = useState({ data: [], pagination: {} })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  useEffect(() => { setPage(1) }, [id])
  useEffect(() => {
    if (!enabled) return
    let active = true
    setLoading(true)
    setError("")
    get(`${couponApi()}/${id}/${resource}`, { params: { page, limit: 25 } }).then(response => {
      if (!Array.isArray(response?.data)) throw new Error("Invalid coupon history response")
      if (active) setResult(response)
    }).catch(failure => {
      if (active) setError(getApiErrorMessage(failure, `Unable to load ${resource}.`))
    }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [id, resource, revision, page, enabled])
  return { ...result, page, setPage, loading, error }
}

function Pagination({ rows, label }) {
  return <div className="d-flex align-items-center justify-content-between gap-3 mt-3">
    <small className="text-muted">Page {rows.page} · {rows.pagination?.total ?? rows.data.length} {label}</small>
    <div className="d-flex gap-2">
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={rows.loading || rows.page <= 1} onClick={() => rows.setPage(value => value - 1)}>Previous {label}</button>
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={rows.loading || rows.pagination?.hasNextPage !== true} onClick={() => rows.setPage(value => value + 1)}>Next {label}</button>
    </div>
  </div>
}

Pagination.propTypes = { rows: PropTypes.object.isRequired, label: PropTypes.string.isRequired }

function CouponInventory({ id, canUpdate, revision, onChange }) {
  const [text, setText] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [importing, setImporting] = useState(false)
  const [showCodes, setShowCodes] = useState(false)
  const lock = useRef(false)
  const rows = useCouponRows(id, "codes", revision, showCodes)
  const importCodes = async event => {
    event.preventDefault()
    if (!canUpdate || lock.current) return
    setError("")
    setSuccess("")
    let codes
    try { codes = parseCouponCodes(text) }
    catch (failure) { setError(failure.message); return }
    lock.current = true
    setImporting(true)
    try {
      const result = await post(`${couponApi()}/${id}/codes`, { codes })
      setText("")
      setSuccess(`${result.data?.imported ?? codes.length} codes imported. Eligible deliveries waiting for stock will be processed automatically.`)
      onChange()
    } catch (failure) {
      setError(getApiErrorMessage(failure, "Unable to import codes. Your entries have been kept."))
    } finally {
      lock.current = false
      setImporting(false)
    }
  }
  return <section className="border rounded p-3 mb-4" aria-label="Code inventory">
    <h4>Code inventory</h4>
    <form onSubmit={importCodes}>
      <label htmlFor="coupon-codes" className="form-label">Import codes — one code per line</label>
      <textarea id="coupon-codes" className="form-control font-monospace" rows={5} value={text} onChange={event => setText(event.target.value)} disabled={!canUpdate || importing} placeholder={"CODE-001\nCODE-002"} />
      <p className="text-muted mt-2">Up to 1,000 codes per import. Duplicate codes reject the entire batch; no partial import is made.</p>
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}
      <div className="d-flex gap-2 flex-wrap">
        <button type="submit" className="btn btn-primary" disabled={!canUpdate || importing || !text.trim()}>{importing ? "Importing..." : "Import codes"}</button>
        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowCodes(value => !value)}>{showCodes ? "Hide inventory" : "View inventory"}</button>
      </div>
      {!canUpdate && <small className="text-muted d-block mt-2">Coupon update permission is required to import codes.</small>}
    </form>
    {showCodes && <div className="mt-4">
      {rows.error && <Alert color="danger">{rows.error}</Alert>}
      {rows.loading ? <Spinner size="sm" aria-label="Loading inventory" /> : !rows.error && <>
        <div className="table-responsive"><table className="table table-bordered"><thead><tr><th>Code</th><th>Allocation</th><th>Imported at</th></tr></thead><tbody>
          {rows.data.map(code => <tr key={code.id}><td className="font-monospace" style={{ overflowWrap: "anywhere" }}>{code.code}</td><td>{code.assigned ? statusLabels[code.status] || "Assigned" : "Available"}</td><td>{displayDate(code.created_at)}</td></tr>)}
          {!rows.data.length && <tr><td colSpan={3}>No codes imported yet.</td></tr>}
        </tbody></table></div>
        <Pagination rows={rows} label="codes" />
      </>}
    </div>}
  </section>
}

CouponInventory.propTypes = { id: PropTypes.string.isRequired, canUpdate: PropTypes.bool.isRequired, revision: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired }

export function canRetryDelivery(delivery) {
  if (!["pending", "failed", "awaiting_stock"].includes(delivery.status)) return false
  if (!delivery.first_attempt_at) return true
  const attemptedAt = new Date(delivery.first_attempt_at).getTime()
  return Number.isFinite(attemptedAt) && Date.now() - attemptedAt < (24 * 60 * 60 - 60) * 1000
}

function CouponDeliveries({ id, canUpdate, revision, onChange }) {
  const rows = useCouponRows(id, "deliveries", revision)
  const [retrying, setRetrying] = useState(null)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const retryLock = useRef(false)
  const retry = async delivery => {
    if (!canUpdate || !canRetryDelivery(delivery) || retryLock.current) return
    retryLock.current = true
    setRetrying(delivery.id)
    setError("")
    setSuccess("")
    try {
      await post(`${couponApi()}/deliveries/${delivery.id}/retry`, {})
      setSuccess("Retry requested for the existing allocation. Refresh the history to check delivery status.")
      onChange()
    } catch (failure) {
      setError(getApiErrorMessage(failure, "Unable to retry delivery."))
      onChange()
    } finally {
      retryLock.current = false
      setRetrying(null)
    }
  }
  return <section aria-label="Delivery history">
    <div className="d-flex justify-content-between align-items-center mb-2"><h4>Delivery history</h4><button type="button" className="btn btn-outline-secondary btn-sm" disabled={rows.loading || retrying !== null} onClick={onChange}>Refresh history</button></div>
    <p className="text-muted">Sent means LINE accepted the message; it does not confirm the recipient read or received it. Retries keep the same assigned code. Manual review requires reconciliation before further sending; automatic retry stops before the 24-hour retry window expires.</p>
    {error && <Alert color="danger">{error}</Alert>}
    {success && <Alert color="success">{success}</Alert>}
    {rows.error && <Alert color="danger">{rows.error}</Alert>}
    {rows.loading ? <Spinner size="sm" aria-label="Loading delivery history" /> : !rows.error && <>
      <div className="table-responsive"><table className="table table-bordered align-middle"><thead><tr><th>LINE UID</th><th>Status</th><th>Created at</th><th>Sent at</th><th>Details</th><th>Action</th></tr></thead><tbody>
        {rows.data.map(delivery => <tr key={delivery.id}>
          <td className="font-monospace" style={{ overflowWrap: "anywhere", maxWidth: 220 }}>{delivery.line_uid}</td>
          <td><span className={`badge bg-${delivery.status === "sent" ? "success" : ["failed", "manual_review"].includes(delivery.status) ? "danger" : "secondary"}`}>{statusLabels[delivery.status] || delivery.status}</span></td>
          <td>{displayDate(delivery.created_at)}</td><td>{delivery.sent_at ? displayDate(delivery.sent_at) : "—"}</td>
          <td style={{ maxWidth: 280, overflowWrap: "anywhere" }}>{delivery.last_error || "—"}</td>
          <td>{canUpdate && canRetryDelivery(delivery) ? <button type="button" className="btn btn-sm btn-outline-primary" disabled={retrying !== null} onClick={() => retry(delivery)} aria-label={`Retry delivery ${delivery.id}`}>{retrying === delivery.id ? "Retrying..." : "Retry delivery"}</button> : delivery.status === "manual_review" || (["pending", "failed"].includes(delivery.status) && !canRetryDelivery(delivery)) ? <span className="text-muted">Review delivery with LINE</span> : "—"}</td>
        </tr>)}
        {!rows.data.length && <tr><td colSpan={6}>No rewards have been allocated yet.</td></tr>}
      </tbody></table></div>
      <Pagination rows={rows} label="deliveries" />
    </>}
  </section>
}

CouponDeliveries.propTypes = { id: PropTypes.string.isRequired, canUpdate: PropTypes.bool.isRequired, revision: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired }

export default function CouponDetail() {
  const { id } = useParams()
  const permissions = useCurrentPermissions("Unable to verify coupon permissions.")
  const canUpdate = permissions.hasScope("coupon:update")
  const [coupon, setCoupon] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [revision, setRevision] = useState(0)
  const refresh = useCallback(() => setRevision(value => value + 1), [])
  useEffect(() => {
    setCoupon(null)
    setLoading(true)
  }, [id])
  useEffect(() => {
    let active = true
    setError("")
    get(`${couponApi()}/${id}`).then(result => {
      if (!result?.data?.id) throw new Error("Invalid coupon response")
      if (active) setCoupon(result.data)
    }).catch(failure => {
      if (active) setError(getApiErrorMessage(failure, "Unable to load coupon details."))
    }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [id, revision])
  const entryUrl = safeEntryUrl(coupon?.campaign?.survey_url)

  return <div className="page-content"><div className="container-fluid">
    <Breadcrumbs title="Coupon / voucher details" breadcrumbItems={[{ title: "Coupons & Vouchers", link: "coupon" }, { title: "Details", link: `view/${id}` }]} />
    <div className="bInnerContent">
      <CampaignPermissionNotice permissions={permissions} permissionLabel="coupon" />
      {error && <Alert color="danger">{error} <button className="btn btn-link p-0" type="button" onClick={refresh}>Try again</button></Alert>}
      {loading ? <Spinner aria-label="Loading coupon details" /> : coupon && String(coupon.id) === String(id) && <>
        <div className="d-flex justify-content-between align-items-start gap-3 mb-3"><div><h3>{coupon.name}</h3><p className="text-muted mb-0">{coupon.type === "voucher" ? "Voucher" : "Coupon"} · {channelLabel(coupon.entry_channel)} · {coupon.active ? "Active" : "Inactive"}</p></div>{canUpdate && <Link className="btn btn-warning" to={`/coupon/edit/${id}`}>Edit settings</Link>}</div>
        <div className="row mb-3">{[["total", "Total codes"], ["available", "Available codes"], ["assigned", "Assigned codes"], ["sent", "Sent rewards"]].map(([key, label]) => <div className="col-6 col-lg-3 mb-2" key={key}><div className="border rounded p-3 h-100"><div className="text-muted">{label}</div><strong className="fs-3">{coupon.counts?.[key] ?? 0}</strong></div></div>)}</div>
        <dl className="row"><dt className="col-md-3">LINE campaign</dt><dd className="col-md-9">{coupon.campaign?.campaign_name || `#${coupon.line_campaign_id}`} · Survey #{coupon.campaign?.survey_id}</dd><dt className="col-md-3">Reward period</dt><dd className="col-md-9">{displayDate(coupon.starts_at)} — {displayDate(coupon.expires_at)} <small className="text-muted">(local time)</small></dd>{coupon.description && <><dt className="col-md-3">Description</dt><dd className="col-md-9">{coupon.description}</dd></>}</dl>
        <div className="border rounded p-3 mb-4"><h5>{channelLabel(coupon.entry_channel)} entry link</h5><p className="text-muted">This link selects the {channelLabel(coupon.entry_channel)} reward within this LINE campaign. Share each channel&apos;s own entry link when a campaign offers both LINE MAN and Grab. One reward per LINE UID per survey applies across LINE MAN and Grab; only successful survey results receive rewards.</p>
          {entryUrl ? <a href={entryUrl} target="_blank" rel="noopener noreferrer" style={{ overflowWrap: "anywhere" }}>{entryUrl}</a> : <span className="text-danger">The LINE entry link is unavailable. Check the campaign and LINE Login settings.</span>}
        </div>
        <CouponInventory id={id} canUpdate={canUpdate} revision={revision} onChange={refresh} />
        <CouponDeliveries id={id} canUpdate={canUpdate} revision={revision} onChange={refresh} />
      </>}
      <Link className="btn btn-outline-secondary mt-4" to="/coupon">Back to coupons</Link>
    </div>
  </div></div>
}
